import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { LUCKY_RUN_NAME, RUN_NAME } from '@goodtime/shared';
import type { BoardRunView } from '../../logic/road/boardRun';
import { stopCounter } from '../../logic/road/boardRun';
import { BoardPanel } from '../board/BoardPanel';
import { TitlePlaque } from '../board/TitlePlaque';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { balancedWrapWidth, sentenceLines } from './balancedWrap';

/**
 * Over the panel during a Wheel Run, in Pixi: the run's name on a plaque,
 * which STOP this is, the lives left (the slot's life token: acorns on
 * Forest), and a status line. "LIVES" is spelt out (producer 2026-10-14:
 * "are they lives or tries or multipliers?"). Short bars put the count
 * beside the lives. A status line that wraps shares its words between its
 * lines, a sentence to a line where they fit, so no word sits alone.
 */
export class RunBar extends Container {
  private readonly board = new BoardPanel();
  private readonly plaque = new TitlePlaque(RUN_NAME.toUpperCase());
  private readonly counter: Text;
  private readonly livesLabel: Text;
  private readonly lives = new Container();
  private readonly status: Text;
  private w = 360;
  private h = 120;
  private view: BoardRunView | null = null;
  private text = '';
  private notice = false;

  constructor(private readonly art: (name: 'life' | 'floor') => Texture | null) {
    super();
    this.counter = new Text({ text: '', style: displayStyle(16, COLOURS.cream) });
    this.livesLabel = new Text({ text: 'LIVES', style: uiStyle(11, 0xf0dcb4) });
    this.status = new Text({ text: '', style: { ...uiStyle(18, 0xffffff), align: 'center', wordWrap: true, lineHeight: 22 } });
    this.status.anchor.set(0.5, 0.5);
    this.addChild(this.board, this.counter, this.livesLabel, this.lives, this.status, this.plaque);
    this.visible = false;
  }

  setLayout(x: number, y: number, width: number, height: number): void {
    this.position.set(Math.round(x), Math.round(y));
    this.w = Math.round(width);
    this.h = Math.round(height);
    this.draw();
  }

  /** The run as it stands, and its status line (or a notice, in red). */
  setRun(view: BoardRunView, status: string, notice: boolean): void {
    this.view = view;
    this.text = status;
    this.notice = notice;
    this.draw();
  }

  refreshArt(): void {
    this.draw();
  }

  /** What the bar says, for tests. */
  get snapshot(): { visible: boolean; title: string; counter: string; status: string; lives: number } {
    return {
      visible: this.visible,
      title: (this.view?.lucky ? LUCKY_RUN_NAME : RUN_NAME).toUpperCase(),
      counter: this.counter.text,
      status: this.status.text.replace(/\n/g, ' '),
      lives: this.view?.lives ?? 0,
    };
  }

  /** The middle of the bar, where coins can fly from. */
  get centre(): { x: number; y: number } {
    return { x: this.x + this.w / 2, y: this.y + this.h / 2 };
  }

  private draw(): void {
    const view = this.view;
    this.board.setFloor(this.art('floor'));
    this.board.resize(this.w, this.h);
    this.plaque.position.set(this.w / 2, 0);
    if (!view) return;
    this.plaque.setTitle((view.lucky ? LUCKY_RUN_NAME : RUN_NAME).toUpperCase());
    const short = this.h < 130;
    const life = Math.round(short ? 30 : Math.min(60, this.h * 0.34));
    const gap = Math.round(life * 0.3);

    this.counter.text = stopCounter(view);
    this.livesLabel.style.fontSize = short ? 11 : Math.round(Math.min(16, life * 0.28));
    this.lives.removeChildren().forEach((c) => c.destroy());
    const token = this.art('life');
    for (let i = 0; i < view.livesMax; i++) {
      if (!token) break;
      const s = new Sprite(token);
      s.width = s.height = life;
      s.x = i * (life + gap);
      const left = i < view.lives;
      s.alpha = left ? 1 : 0.3;
      s.tint = left ? 0xffffff : 0x777777;
      this.lives.addChild(s);
    }
    const livesW = view.livesMax * life + (view.livesMax - 1) * gap;
    this.status.style.fill = this.notice ? COLOURS.loss : 0xffffff;
    this.status.style.fontSize = short ? 15 : 18;
    const room = this.w - 28;
    const style = { ...uiStyle(short ? 15 : 18), lineHeight: 22 };
    const shown = sentenceLines(this.text, style, room);
    this.status.text = shown;
    this.status.style.wordWrapWidth = balancedWrapWidth(shown, style, room);

    if (short) {
      // One row: the count, then LIVES and the tokens; the status under it.
      const rowY = 16 + life / 2;
      const rowW = this.counter.width + 18 + this.livesLabel.width + 8 + livesW;
      let x = (this.w - rowW) / 2;
      this.counter.position.set(x, rowY - this.counter.height / 2);
      x += this.counter.width + 18;
      this.livesLabel.position.set(x, rowY - this.livesLabel.height / 2);
      x += this.livesLabel.width + 8;
      this.lives.position.set(x, rowY - life / 2);
      this.status.position.set(this.w / 2, (rowY + life / 2 + this.h) / 2);
    } else {
      // Stacked: the count, the lives with LIVES beside them, the status.
      this.counter.position.set((this.w - this.counter.width) / 2, 16);
      const livesY = 16 + this.counter.height + 4;
      const rowW = this.livesLabel.width + 10 + livesW;
      this.livesLabel.position.set((this.w - rowW) / 2, livesY + life / 2 - this.livesLabel.height / 2);
      this.lives.position.set(this.livesLabel.x + this.livesLabel.width + 10, livesY);
      this.status.position.set(this.w / 2, (livesY + life + this.h) / 2);
    }
  }
}
